import React, { useState, useEffect } from 'react';
import Button from '../common/Button';
import LoadingSpinner from '../common/LoadingSpinner';
import CheckInButton from '../reservations/CheckInButton';

const UpcomingReservations = ({ onReservationsLoaded }) => {
  const [reservations, setReservations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUpcomingReservations();
  }, []);

  const loadUpcomingReservations = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/reservations/my-reservations?status=active`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        // Handle different types of errors
        if (response.status === 401) {
          throw new Error('Please log in again to view your reservations');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view reservations');
        } else if (response.status >= 500) {
          throw new Error('Server error - please try again later');
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Upcoming reservations API error:', response.status, errorData);
          throw new Error(errorData.error || `Failed to load upcoming reservations (HTTP ${response.status})`);
        }
      }

      const data = await response.json();
      
      // Ensure data is an array
      const reservationsArray = Array.isArray(data) ? data : [];
      
      // Filter to upcoming reservations only and limit to 3
      const now = new Date();
      const upcomingReservations = reservationsArray
        .filter(reservation => {
          // Only show truly upcoming reservations (start time in future and status is active)
          return new Date(reservation.start_time) > now && 
                 (reservation.status === 'active' || reservation.status === 'checked_in');
        })
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
        .slice(0, 3);
      
      setReservations(upcomingReservations);
      
      // Notify parent component about reservations count
      if (onReservationsLoaded) {
        onReservationsLoaded(upcomingReservations.length);
      }
    } catch (error) {
      console.error('Error loading upcoming reservations:', error);
      setError(error.message);
      
      // Notify parent that loading failed (treat as 0 reservations)
      if (onReservationsLoaded) {
        onReservationsLoaded(0);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    let dateLabel;
    if (date.toDateString() === today.toDateString()) {
      dateLabel = 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dateLabel = 'Tomorrow';
    } else {
      dateLabel = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    }

    return {
      date: dateLabel,
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const canCheckIn = (reservation) => {
    const now = new Date();
    const startTime = new Date(reservation.start_time);
    const endTime = new Date(reservation.end_time);
    const checkInWindow = 10 * 60 * 1000; // 10 minutes in milliseconds
    
    return reservation.status === 'active' && 
           now >= (startTime.getTime() - checkInWindow) && 
           now <= endTime.getTime();
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl p-6 border-2 border-slate-900 shadow-retro">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
          <span className="w-1 h-6 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
          Upcoming Reservations
        </h3>
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="medium" text="Loading your upcoming reservations..." />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl p-6 border-2 border-slate-900 shadow-retro">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-4">
          <span className="w-1 h-6 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
          Upcoming Reservations
        </h3>
        <div className="text-center py-8">
          <p className="text-sm text-coral-600 font-medium">{error}</p>
          <Button onClick={loadUpcomingReservations} size="small" className="mt-3">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (reservations.length === 0) {
    return null; // Don't show the section if there are no reservations
  }

  return (
    <div className="bg-white rounded-xl p-6 border-2 border-slate-900 shadow-retro">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <span className="w-1 h-6 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
          Upcoming Reservations
        </h3>
        <Button
          onClick={() => window.location.href = '/reservations'}
          variant="outline"
          size="small"
        >
          View All
        </Button>
      </div>

      <div className="space-y-4">
        {reservations.map((reservation) => {
          const dateTime = formatDateTime(reservation.start_time);
          const endTime = formatDateTime(reservation.end_time);

          return (
            <div key={reservation.id} className="border-2 border-slate-900 rounded-lg p-4 hover:bg-teal-50 hover:border-teal-500 shadow-retro hover:shadow-retro-hover transition-all">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex-1">
                  {/* Date and Time - More Prominent */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-gradient-to-br from-teal-500 to-purple-500 rounded-lg p-2 border-2 border-slate-900">
                      <svg className="w-4 h-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-slate-900">
                        {dateTime.date}
                      </p>
                      <p className="text-sm font-semibold text-slate-700">
                        {dateTime.time} - {endTime.time}
                      </p>
                    </div>
                  </div>

                  {/* Board Details */}
                  <div>
                    <h4 className="text-base font-bold text-slate-900 capitalize">
                      {reservation.board_name}
                    </h4>
                    <p className="text-xs text-slate-600 font-medium">
                      {reservation.gym_name}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {canCheckIn(reservation) && (
                    <CheckInButton
                      reservationId={reservation.id}
                      onCheckIn={loadUpcomingReservations}
                      size="small"
                    />
                  )}
                  <Button
                    onClick={() => window.location.href = `/board/${reservation.board_id}`}
                    variant="outline"
                    size="small"
                  >
                    Update Reservation
                  </Button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpcomingReservations;